// Bot + email diagnostics panel. Admin-only — surfaces every link in
// the planner notification chain (env vars → migration → bot install →
// notify prefs → live test send) so an operator can see exactly where
// the pipeline breaks without tailing App Service log stream. Mounted
// from the planner header next to the Graph diag icon.
import { useEffect, useState } from "react";
import { CheckCircle2, RefreshCw, Send, XCircle } from "lucide-react";
import { Sheet } from "../components/Sheet";
import { tpApi } from "../api";
import { DC } from "./createSlideOver/primitives";

type Diag = Awaited<ReturnType<typeof tpApi.notifyDiag>>;
type TestResult = Awaited<ReturnType<typeof tpApi.notifyTest>>;

export function BotDiagPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [data, setData] = useState<Diag | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    void tpApi
      .notifyDiag()
      .then((d) => setData(d))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [open, tick]);

  const runTest = () => {
    setTesting(true);
    setTestResult(null);
    void tpApi
      .notifyTest()
      .then((r) => setTestResult(r))
      .catch((e) =>
        setTestResult({
          teams: { ok: false, reason: e instanceof Error ? e.message : String(e) },
          email: { ok: false, reason: e instanceof Error ? e.message : String(e) },
        }),
      )
      .finally(() => setTesting(false));
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Notification diagnostics"
      footer={
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="bsp-btn bsp-btn-secondary bsp-btn-sm"
            onClick={() => setTick((n) => n + 1)}
          >
            <RefreshCw size={12} /> Re-run
          </button>
          <button
            type="button"
            className="bsp-btn bsp-btn-primary bsp-btn-sm"
            onClick={runTest}
            disabled={testing || !data}
            title="Send a test card to your Teams chat + a test email to your address"
          >
            <Send size={12} /> {testing ? "Sending…" : "Send test"}
          </button>
        </div>
      }
    >
      {loading ? (
        <p style={{ color: DC.grey }}>Running diagnostics…</p>
      ) : error ? (
        <div style={errorBox}>
          <strong>Diag request failed:</strong> {error}
          <div style={{ fontSize: 12, marginTop: 6 }}>
            This endpoint is gated on the ToolsHub.Admin role.
          </div>
        </div>
      ) : !data ? null : (
        <>
          <Section title="Teams bot — config">
            <Check label="MICROSOFT_APP_ID set" ok={data.bot.config.hasAppId} />
            <Check
              label={
                data.bot.config.appType === "UserAssignedMSI"
                  ? "MICROSOFT_APP_TYPE = UserAssignedMSI (password not required)"
                  : "MICROSOFT_APP_PASSWORD set"
              }
              ok={
                data.bot.config.appType === "UserAssignedMSI" ||
                data.bot.config.hasAppPassword
              }
            />
            <Check
              label={`MICROSOFT_APP_TYPE = ${data.bot.config.appType}`}
              ok={true}
            />
            <Check
              label="Bot runtime configured"
              ok={data.bot.configured}
              hint={
                data.bot.configured
                  ? "getTeamsBot() returns a live adapter. /api/messages will validate inbound activities."
                  : "Add the missing env vars in App Service → Configuration → Application settings, then restart the slot."
              }
            />
            {data.bot.messagesEndpoint ? (
              <div style={{ marginTop: 8, fontSize: 12 }}>
                <strong>Bot messaging endpoint should be:</strong>
                <pre style={codeBox}>{data.bot.messagesEndpoint}</pre>
                <div style={{ color: DC.grey, marginTop: 4 }}>
                  Set this on the Azure Bot resource → Configuration →
                  Messaging endpoint. If it points at a different host
                  (an old slot, a tunnel, etc.) the install event never
                  reaches this app and no conversation reference is
                  captured.
                </div>
              </div>
            ) : null}
          </Section>

          <Section title="Teams bot — database">
            <Check
              label="planner_bot_conversation table exists"
              ok={data.bot.tableExists}
              hint={
                data.bot.tableExists
                  ? `Migration 0007 has been applied. ${data.bot.totalConversations} user${
                      data.bot.totalConversations === 1 ? " has" : "s have"
                    } installed the bot.`
                  : "Migration 0007_planner_bot_conversation has NOT been applied. Run the migrate workflow before installing the bot — captureReference silently fails without this table."
              }
            />
            <Check
              label="Your conversation reference captured"
              ok={Boolean(data.viewer.botConversation)}
              hint={
                data.viewer.botConversation
                  ? `Captured ${new Date(
                      data.viewer.botConversation.capturedAt,
                    ).toLocaleString()}${
                      data.viewer.botConversation.lastSentAt
                        ? `. Last send ${new Date(
                            data.viewer.botConversation.lastSentAt,
                          ).toLocaleString()}.`
                        : ". No proactive sends yet."
                    }`
                  : "Install the BSP Team Planner app in personal scope inside Teams. The bot DMs you a welcome message and writes a row to planner_bot_conversation. If you've already installed it, uninstall and re-install — the install event may have hit a stale messaging endpoint."
              }
            />
          </Section>

          <Section title="Email — config">
            <Check label="EMAIL_FROM_ADDRESS set" ok={data.email.config.hasFromAddress} />
            <Check label="GRAPH_TENANT_ID set" ok={data.email.config.hasGraphTenant} />
            <Check label="GRAPH_CLIENT_ID set" ok={data.email.config.hasGraphClient} />
            <Check label="GRAPH_CLIENT_SECRET set" ok={data.email.config.hasGraphSecret} />
            <Check
              label="Email channel configured"
              ok={data.email.configured}
              hint={
                data.email.configured
                  ? "sendMail will be called via the API app reg's Mail.Send (application) permission."
                  : "Either EMAIL_FROM_ADDRESS or one of the GRAPH_* vars is missing."
              }
            />
          </Section>

          <Section title="Your notification preferences">
            <Check
              label={
                data.viewer.notifyPrefs.defaulted
                  ? "No preference row — using opted-in defaults"
                  : "Preference row exists"
              }
              ok={true}
            />
            <Check
              label="notifyViaTeams"
              ok={data.viewer.notifyPrefs.notifyViaTeams}
              hint={
                data.viewer.notifyPrefs.notifyViaTeams
                  ? undefined
                  : "Teams channel is toggled OFF in your Notification Preferences."
              }
            />
            <Check
              label="notifyViaEmail"
              ok={data.viewer.notifyPrefs.notifyViaEmail}
              hint={
                data.viewer.notifyPrefs.notifyViaEmail
                  ? undefined
                  : "Email channel is toggled OFF in your Notification Preferences."
              }
            />
            <div style={{ fontSize: 12, color: DC.grey, marginTop: 6 }}>
              digestMode: <strong>{data.viewer.notifyPrefs.digestMode}</strong>
              {data.viewer.notifyPrefs.quietHoursStart
                ? ` · quiet hours ${data.viewer.notifyPrefs.quietHoursStart}–${data.viewer.notifyPrefs.quietHoursEnd} (${data.viewer.notifyPrefs.timezone ?? "UTC"})`
                : " · no quiet hours set"}
            </div>
            <div style={{ fontSize: 11, color: DC.grey, marginTop: 4 }}>
              Note: digestMode ≠ "immediate" suppresses immediate push.
              The digest buffer + flush isn't implemented yet, so anything
              other than "immediate" effectively silences your channel
              until that lands.
            </div>
          </Section>

          <Section title="Your last 5 activity events (as actor)">
            {data.viewer.recentActivity.length === 0 ? (
              <p style={{ fontSize: 12, color: DC.grey, margin: 0 }}>
                No activity recorded. Nothing has called recordActivity()
                with you as the actor — try assigning a rock or posting
                a comment, then re-run.
              </p>
            ) : (
              <ul style={{ margin: 0, padding: "0 0 0 18px", fontSize: 12 }}>
                {data.viewer.recentActivity.map((a, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    <strong>{a.verb}</strong> on {a.itemKind} ·{" "}
                    {new Date(a.createdAt).toLocaleString()}
                  </li>
                ))}
              </ul>
            )}
            <p style={{ fontSize: 11, color: DC.grey, marginTop: 6 }}>
              Activity events drive the fan-out. Remember the actor is
              excluded from their own notifications — to receive a card
              you need a different account to trigger the activity.
            </p>
          </Section>

          {testResult ? (
            <Section title="Test send result">
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                Teams card
              </div>
              {testResult.teams.ok ? (
                <Check label="Card delivered to Bot Connector" ok />
              ) : (
                <>
                  <Check label="Card send failed" ok={false} />
                  <pre style={errorCodeBox}>
                    {"code" in testResult.teams && testResult.teams.code
                      ? `${testResult.teams.code}\n\n`
                      : ""}
                    {testResult.teams.reason}
                    {"status" in testResult.teams && testResult.teams.status
                      ? `\n\nHTTP status: ${testResult.teams.status}`
                      : ""}
                  </pre>
                </>
              )}
              <div
                style={{ fontSize: 13, fontWeight: 600, margin: "14px 0 4px" }}
              >
                Email
              </div>
              {testResult.email.ok ? (
                <Check label="Email accepted by Graph sendMail" ok />
              ) : (
                <>
                  <Check label="Email send failed" ok={false} />
                  <pre style={errorCodeBox}>
                    {testResult.email.reason}
                    {"status" in testResult.email && testResult.email.status
                      ? `\n\nHTTP status: ${testResult.email.status}`
                      : ""}
                  </pre>
                </>
              )}
            </Section>
          ) : null}
        </>
      )}
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 18 }}>
      <div
        style={{
          font: "700 10px/1 var(--bsp-font-sans)",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: DC.grey,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

function Check({ label, ok, hint }: { label: string; ok: boolean; hint?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
      {ok ? (
        <CheckCircle2 size={16} color="var(--bsp-green)" />
      ) : (
        <XCircle size={16} color="var(--bsp-red)" />
      )}
      <div>
        <div style={{ fontSize: 13 }}>{label}</div>
        {hint ? (
          <div style={{ fontSize: 11, color: DC.grey, marginTop: 2 }}>{hint}</div>
        ) : null}
      </div>
    </div>
  );
}

const errorBox: React.CSSProperties = {
  padding: 12,
  background: "var(--bsp-red-tint)",
  border: "1px solid var(--bsp-red)",
  borderRadius: 6,
  color: "var(--bsp-red-fg)",
  fontSize: 13,
  lineHeight: 1.4,
};
const codeBox: React.CSSProperties = {
  margin: "4px 0 0",
  padding: 8,
  background: "var(--bsp-surface-muted)",
  border: "1px solid var(--bsp-border)",
  borderRadius: 4,
  fontSize: 12,
  whiteSpace: "pre-wrap",
  fontFamily: "var(--bsp-font-mono)",
};
const errorCodeBox: React.CSSProperties = {
  margin: "8px 0 0",
  padding: 10,
  background: "var(--bsp-surface-muted)",
  border: "1px solid var(--bsp-border)",
  borderRadius: 4,
  fontSize: 12,
  whiteSpace: "pre-wrap",
  fontFamily: "var(--bsp-font-mono)",
};
